"use client";

/**
 * Context provider for competition home page data.
 *
 * Provides home page data and the server-computed competition slices
 * (standings, home match slices, season stats) to all child components,
 * allowing them to read them without prop drilling.
 */

import React, { createContext, useContext, useMemo } from "react";
import type { CompetitionHomePageData, CompetitionSliceData } from "./types";

/**
 * Extended context value with computed helpers.
 */
export interface CompetitionHomeDataContextValue extends CompetitionHomePageData {
    /** Check if there are news articles */
    hasNews: boolean;
    /** Primary-stage standings. */
    standings: CompetitionSliceData["standings"];
    homeSlices: CompetitionSliceData["homeSlices"];
    competitionStats: CompetitionSliceData["competitionStats"];
    goalDistribution: CompetitionSliceData["goalDistribution"];
    /** Whether the season has finished matches to show stats for. */
    hasCompetitionStats: boolean;
}

const CompetitionHomeDataContext = createContext<CompetitionHomeDataContextValue | null>(null);

interface CompetitionHomeDataProviderProps {
    data: CompetitionHomePageData;
    competitionData: CompetitionSliceData;
    children: React.ReactNode;
}

/**
 * Provider component that makes home page data available to all child components.
 *
 * Usage in a server component:
 * ```tsx
 * const { homeData, competitionData } = await getCompetitionHomePageData(options);
 *
 * return (
 *     <CompetitionSliceProvider value={{ competition, seasonName, nativeCompetitionId }}>
 *         <CompetitionHomeDataProvider data={homeData} competitionData={competitionData}>
 *             <CompetitionHomeLayout />
 *         </CompetitionHomeDataProvider>
 *     </CompetitionSliceProvider>
 * );
 * ```
 * `CompetitionSliceProvider` sits outside because some layouts (DataOnly) render standings tabs that read
 * `useCompetitionSlice()`.
 */
export function CompetitionHomeDataProvider({ data, competitionData, children }: CompetitionHomeDataProviderProps) {
    const contextValue = useMemo<CompetitionHomeDataContextValue>(() => {
        return {
            ...data,
            hasNews: data.latestNews.length > 0,
            standings: competitionData.standings,
            homeSlices: competitionData.homeSlices,
            competitionStats: competitionData.competitionStats,
            goalDistribution: competitionData.goalDistribution,
            hasCompetitionStats: competitionData.competitionStats.finishedMatches > 0,
        };
    }, [data, competitionData]);

    return <CompetitionHomeDataContext.Provider value={contextValue}>{children}</CompetitionHomeDataContext.Provider>;
}

/**
 * Hook to access competition home page data in client components.
 * Must be used within a CompetitionHomeDataProvider.
 *
 * @throws Error if used outside of CompetitionHomeDataProvider
 */
export function useCompetitionHomeData(): CompetitionHomeDataContextValue {
    const context = useContext(CompetitionHomeDataContext);

    if (!context) {
        throw new Error("useCompetitionHomeData must be used within a CompetitionHomeDataProvider");
    }

    return context;
}

/**
 * Hook to optionally access competition home page data.
 * Returns null if not within a CompetitionHomeDataProvider (useful for shared components).
 */
export function useCompetitionHomeDataOptional(): CompetitionHomeDataContextValue | null {
    return useContext(CompetitionHomeDataContext);
}
